/**
 * Ctrl+Q — la déconnexion au clavier.
 *
 * POURQUOI UN RACCOURCI DE DÉCONNEXION
 * ---------------------------------------------------------------------------
 * Un poste partagé se quitte vite ou ne se quitte pas. Trois clics dans un menu
 * — ouvrir le widget, trouver l'entrée, confirmer — suffisent à ce que
 * quelqu'un laisse sa session ouverte « juste deux minutes ». Une combinaison
 * qui ferme tout de suite enlève ce prétexte.
 *
 * CE QU'IL FAUT SAVOIR AVANT DE S'Y FIER
 * ---------------------------------------------------------------------------
 * `Ctrl+Q` n'appartient pas à la page : Firefox l'utilise pour quitter le
 * navigateur, macOS pour quitter l'application, et aucun `preventDefault` ne
 * reprend la main dessus. Là où le navigateur le garde, la page ne le voit
 * jamais — ce n'est donc pas une garantie, c'est un raccourci. Le menu du
 * widget reste la voie qui marche partout, et il ne bouge pas.
 *
 * CE QUE LE RACCOURCI NE DOIT PAS FAIRE
 * ---------------------------------------------------------------------------
 * Partir pendant une saisie. Une combinaison qui vide un formulaire à demi
 * rempli fait perdre un travail que personne ne pourra retrouver — et ce sera
 * lu comme un bogue, pas comme une sécurité. D'où le refus quand le focus est
 * dans un champ.
 */

/** Vrai quand le focus est dans une zone de saisie : on ne coupe pas. */
function saisieEnCours(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false
  const balise = cible.tagName
  return (
    balise === 'INPUT' ||
    balise === 'TEXTAREA' ||
    balise === 'SELECT' ||
    cible.isContentEditable
  )
}

/**
 * Installe le raccourci, et rend la fonction pour le retirer.
 *
 * `deconnecter` n'est appelé qu'une fois : sans ce verrou, un appui maintenu
 * répète l'événement (`repeat`) et lancerait plusieurs déconnexions
 * concurrentes, dont les réponses arriveraient dans le désordre.
 */
export function installerRaccourciDeconnexion(options: {
  deconnecter: () => void | Promise<void>
  estConnecte: () => boolean
}): () => void {
  let enCours = false

  function surTouche(evenement: KeyboardEvent): void {
    // `evenement.key` et non `keyCode` : sur un clavier AZERTY comme QWERTY,
    // la touche produit « q ». Comparer un code physique ferait dépendre le
    // raccourci de la disposition.
    if (evenement.key !== 'q' && evenement.key !== 'Q') return
    if (!evenement.ctrlKey && !evenement.metaKey) return
    // `altKey` exclu : Ctrl+Alt+Q est une combinaison d'un autre logiciel, pas
    // la nôtre — l'intercepter volerait un raccourci qu'on n'a pas demandé.
    if (evenement.altKey || evenement.shiftKey) return

    if (evenement.repeat || enCours) return
    if (saisieEnCours(evenement.target)) return
    if (!options.estConnecte()) return

    // Réclamé seulement quand on va VRAIMENT agir : un `preventDefault` posé
    // plus haut mangerait le raccourci du navigateur même dans les cas où l'on
    // décide de ne rien faire.
    evenement.preventDefault()
    enCours = true
    void Promise.resolve(options.deconnecter()).finally(() => {
      enCours = false
    })
  }

  window.addEventListener('keydown', surTouche)
  return () => window.removeEventListener('keydown', surTouche)
}
